import React, { useState, useRef, useEffect, useImperativeHandle, forwardRef } from 'react';
import { Typography, Icon } from '@material-ui/core';
import useStyles from './ObservationCreate.styles';
import { get, set, sortBy, first, size, unset } from 'global/lib/lodash';
import Form from 'global/components/form';
import Styles, { Loader } from 'global/styles';
import clsx from 'clsx';
import ScoreTag from './ScoreTag';
import sendData from 'redux/actions/sendData';
import useRetrieveData from 'global/hooks/useRetrieveData';
import CheckpointForm from 'global/components/form-checkpoint';
import { UploadFiles } from '../../../../redux/actions/imageUploader';
import { ScorecardScoreIncludes } from '../../../../global/hooks/useQueue';
import { FullScreen } from '../../../../global/components/modal';
import { Button } from '../../../../global/components/Button';
import { wait } from '../../../../global/utils';
import useDebounce from '../../../../global/hooks/useDebounce';
import ScoreIcon from 'global/components/ScoreIcon';
import { Row } from 'global/components/row';

const urlParams = { include: 'scorecardOptions' };

const EditScore = (props) => {
  const classes = useStyles();
  const { checkpoint, checklist, assessment, scorecard, onScore } = props;

  const [params] = useState({
    useEffect: true,
    params: urlParams,
    build: ['checkpoints'],
    transformData: (d) => get(d, '0'),
  });

  const checkpointdata = useRetrieveData({ url: `/checkpoints/${get(checkpoint, 'checkpointId')}` }, params);

  return checkpoint.error ? (
    <div>{`Error: ${get(checkpoint, 'error.message')}`}</div>
  ) : (
    <div className={classes.topic}>
      <CheckpointForm
        isCourse={false}
        addToQueue={onScore}
        queue={[]}
        checkpoint={checkpoint}
        assessmentId={get(assessment, 'data.id')}
        checklist={checklist}
        checkpointdata={checkpointdata}
        loading={checkpoint.loading}
        scorecard={scorecard}
        assessment={assessment}
        scoreOnly={true}
        scoreOnlyIndex={props.scoreOnlyIndex}
      />
    </div>
  );
};

const buildTagFromCheckpoint = (cp) => {
  if (!cp) {
    return null;
  }

  const tag = {
    system: {},
    checklist: {},
    checkpoint: {
      checkpoint: {},
    },
  };

  set(tag, 'system', { id: cp.supersetId });
  set(tag, 'checklist', { ...cp.checklist, supersetId: cp.supersetId });
  set(tag, 'checkpoint.checkpoint', cp);

  return tag;
};

export const SubscoreForm = forwardRef((props, ref) => {
  const { rooms, loading, assessment } = props;

  const [formState, setFormState] = useState(props.formState);
  const [score, setScore] = useState({});
  const [tag, setTag] = useState();
  const [tagModal, setTagModal] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [fs, setfs] = useState({});
  const debfs = useDebounce(fs, 1000);
  const submits = useRef(0);

  const formRef = useRef();

  const classes = useStyles();

  const subscore = get(props, 'checkpoint._subscore');
  const assessmentRealmId = get(assessment, 'data.realmId');

  const onScore = (d) => {
    setScore(d.build());
  };

  // const clearTag = () => {
  //   setTag();
  //   setTagModal(true);
  // };

  const onTagSelected = (tags) => {
    setTag(tags);
  };

  const submitSubScore = async () => {
    let realm = get(formState, 'realm');

    const isNewrealm = typeof realm === 'string' || realm instanceof String;
    if (isNewrealm) {
      const result = await sendData(
        {
          url: `/realms`,
          method: 'post',
          data: {
            data: {
              type: 'realms',
              attributes: {
                name: realm,
                tier: get(rooms, 'realmFilter.tier', 'room'),
                parent_id: get(first(rooms.data), 'parentId') || assessmentRealmId,
              },
            },
          },
        },
        { normalize: { build: ['realms'] } }
      );

      get(rooms, 'update', () => 0)();
      realm = first(result);
    }

    const data = {
      data: {
        type: 'sub-scores',
        id: get(subscore, 'id'),
        attributes: {
          ...get(score, 'data.attributes', {}),
          criteria: get(tag, 'checkpoint.checkpoint.criteria', ''),
          realm_id: get(realm, 'id'),
          notes: get(formState, 'notes', '') || '',
          images: get(formState, 'images', []),
        },
      },
    };

    await UploadFiles(data);

    return await createSubScore(data);
  };

  useEffect(() => {
    const hasFormState = !!subscore || get(props, 'formState.realm');
    const propState = subscore || get(props, 'formState');
    let tag;
    if (hasFormState) {
      setFormState(propState);
      tag = get(propState, 'tag', buildTagFromCheckpoint(props.checkpoint));
    }
    setTag(tag);
    if (!tag || props.openTagToSelected) {
      setTagModal(true);
    }
    setHasLoaded(true);
  }, []);

  useEffect(() => {
    const hasAllVals = !!debfs.realm && !!debfs.tag && debfs.score;
    if (hasAllVals) {
      submits.current += 1;
      props.onFormReady && props.onFormReady({ formState: fs, submits: submits.current, submitSubScore });
    }
  }, [debfs]);

  useEffect(() => {
    const _fs = { ...formState, score, tag };
    setfs(_fs);
    props.onFormState && props.onFormState(_fs);
  }, [score, formState, tag]);

  useEffect(() => {
    if (!tag) {
      setScore({});
    }
  }, [tag]);

  useImperativeHandle(
    ref,
    () => ({
      submitSubScore,
    }),
    [tag, formState, score]
  );

  const superset_id = get(tag, 'checklist.supersetId');
  const canChangeTag = get(props, 'canChangeTag', true);

  return !hasLoaded ? null : (
    <div className={classes.formContainer}>
      {canChangeTag && (
        <div className={classes.buttonContainer}>
          <Button variant="outlined" startIcon={<Icon>{tag ? 'close' : 'label'}</Icon>} onClick={() => setTagModal(true)} className={clsx(classes.tagButton, tag && classes.tagRemove)}>
            {tag ? 'Change Criteria' : 'Link Criteria'}
          </Button>
        </div>
      )}

      {tag && (
        <>
          <Row>
            <ScoreIcon score={get(tag, 'checkpoint.checkpoint.value', 0)} />
            <Typography variant={'h6'}>{`${get(tag, 'checklist.name', get(tag, 'checklist.checklist.name', ''))}: ${get(tag, 'checkpoint.checkpoint.name')}`}</Typography>
          </Row>
          <Typography variant={'body1'}>{`${get(tag, 'checkpoint.checkpoint.criteria', '')}`}</Typography>
          <EditScore
            {...props}
            key={get(tag, 'checkpoint.checkpoint.id')}
            scoreOnlyIndex={subscore ? { 0: 0, 100: 1, '-1': -1 }[subscore.score] : undefined}
            onScore={onScore}
            system={get(tag, 'system')}
            checklist={get(tag, 'checklist')}
            checkpoint={get(tag, 'checkpoint.checkpoint')}
          />
        </>
      )}

      <Form
        loading={loading}
        showAllScreens={true}
        onFormState={setFormState}
        ref={formRef}
        screens={[
          {
            title: '',
            inputs: [
              {
                id: 'realm',
                type: 'Autocomplete',
                defaultValue: () => get(formState, 'realm'),
                data: () => get(rooms, 'data', []),
                props: {
                  label: 'Location',
                },
              },
              {
                id: 'notes',
                type: 'TextField',
                defaultValue: () => get(formState, 'notes'),
                props: {
                  placeholder: 'Describe your observation',
                  rows: 5,
                  multiline: true,
                },
              },
              {
                id: 'images',
                type: 'DropArea',
                defaultValue: () => sortBy(get(formState, 'images', []), 'orderColumn'),
                props: {
                  label: 'Images',
                  sortable: true,
                  shouldUpdateOnCaptionChange: true,
                  imageSelect: {
                    assessment: get(props, 'assessment.data'),
                    superset_id: superset_id,
                    disabled: !superset_id,
                  },
                },
              },
            ],
          },
        ]}
      />

      <ScoreTag {...props} tag={tag} open={tagModal} onTagSelected={onTagSelected} onClose={() => setTagModal(false)} />
    </div>
  );
});

export const ObservationCreateModal = (props) => {
  const { onClose, open, updateScores } = props;
  const [formLoading, setFormLoading] = useState(false);
  const [formState, setFormState] = useState({});
  const formRef = useRef();

  const subscore = get(props, 'checkpoint._subscore');

  const classes = useStyles();
  const styles = Styles();

  const handleClose = () => {
    onClose();
  };

  const formSubmit = async (addAnother = false) => {
    setFormLoading(true);
    const result = await formRef.current.submitSubScore();

    await updateScores(result);

    if (addAnother) {
      setFormState({ realm: get(formState, 'realm'), tag: get(formState, 'tag') });
    } else {
      handleClose();
    }
    setFormLoading(false);
  };

  useEffect(() => {
    if (!open) {
      setFormState({});
    }
  }, [open]);

  const disabled = !formState.tag || !get(formState, 'realm') || !(get(formState, 'notes') || !!size(get(formState, 'images')));
  const showAdd = get(props, 'showAdd', true);

  return (
    <FullScreen
      open={open}
      onClose={handleClose}
      title={get(props, 'checkpoint._subscore') && !get(props, 'checkpoint._subscore.isadd') ? 'Edit Observation' : 'Add Observation'}
      endIcon="place"
      dialogActions={{ style: { alignItems: 'center', justifyContent: 'center' } }}
      buttons={[
        showAdd && {
          title: 'Save & Add',
          onClick: () => formSubmit(true),
          startIcon: (
            <div className={classes.doubleIcon}>
              <Icon>check_plus</Icon>
              <Icon className={classes.plusIcon}>add</Icon>
            </div>
          ),
          disabled,
        },
        { title: 'Save & Close', onClick: () => formSubmit(), startIcon: 'check', disabled },
      ].filter((f) => !!f)}
    >
      <div className={classes.container}>
        {!formLoading && !!open && <SubscoreForm {...props} ref={formRef} formState={formState} onFormState={setFormState} />}

        {formLoading && (
          <div className={clsx(classes.container, styles.centerColumn)}>
            <Loader size={40} />
          </div>
        )}
      </div>
    </FullScreen>
  );
};

export const deleteSubScore = async (score) => {
  await sendData({
    url: `/sub-scores/${score.id}`,
    method: 'patch',
    data: {
      data: {
        id: score.id,
        type: 'sub-scores',
        attributes: {
          archived: 1,
        },
      },
    },
  });

  return getSubProcessedScore(score);
};

export const modifySubScore = async (score) => {
  await sendData({
    url: `/sub-scores/${score.id}`,
    method: 'patch',
    data: {
      data: {
        id: score.id,
        type: 'sub-scores',
        attributes: {
          ...score,
          id: undefined,
        },
      },
    },
  });

  return getSubProcessedScore(score);
};

export const getSubProcessedScore = async (score) => {
  const scscore = first(
    await sendData(
      {
        url: `/scorecard-scores/${score.scorecardScoreId}${ScorecardScoreIncludes}`,
        method: 'get',
      },
      { normalize: { build: ['scorecardScores'] } }
    )
  );

  set(
    scscore,
    'processedScorecardScoreValue.subscores',
    get(scscore, 'processedScorecardScoreValue.subScores', []).map((ss) => formatSubScore(ss, get(scscore, 'processedScorecardScoreValue')))
  );

  return [scscore];
};

export const createSubScore = async (data) => {
  const hasId = get(data, 'data.id');
  const result = await sendData(
    {
      url: `/sub-scores/${hasId || ''}?include=images,realm`,
      method: hasId ? 'patch' : 'post',
      data,
    },
    { normalize: { build: ['subScores'] } }
  );

  return getSubProcessedScore(first(result));
};

export const formatSubScore = (score, parent = {}) => {
  const obj = {
    ...parent,
    ...score,
    checklist: { ...get(parent, 'checklist', get(parent, 'parent', {})) },
    subScores: undefined,
    value: get(score, 'value', score.score),
    images: (score.images || []).map((img) => ({
      ...img,
      thumb: img.thumbUrl,
      full: img.fullUrl,
      large: img.largeUrl,
    })),
    isSubScore: true,
    notes: score.notes,
    _subscore: score,
  };
  set(obj, '_subscore.notes', score.notes);
  unset(obj, 'parentId');
  unset(obj, 'parent');
  unset(obj, 'checklist.parentId');
  unset(obj, 'checklist.parent');
  unset(obj, 'checklist.children');
  return obj;
};
